const ALLOWED_TAGS = new Set([
  "P",
  "BR",
  "STRONG",
  "B",
  "EM",
  "I",
  "U",
  "A",
  "SPAN",
]);
const ALLOWED_LINK_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);

// Disallowed elements are normally unwrapped so their text survives (correct for <div>, <h1>, …).
// These carry code rather than prose, so their content must be discarded too — otherwise the code
// shows up as visible text. A paste from Word commonly carries a <style> block, which is how this
// surfaces in practice.
const DROPPED_WITH_CONTENT = new Set([
  "SCRIPT",
  "STYLE",
  "TEMPLATE",
  "NOSCRIPT",
  "IFRAME",
  "OBJECT",
  "EMBED",
]);

function sanitizeNode(node: Node): Node | null {
  if (node.nodeType === Node.TEXT_NODE) {
    return document.createTextNode(node.textContent ?? "");
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const element = node as HTMLElement;
  const tagName = element.tagName.toUpperCase();

  if (DROPPED_WITH_CONTENT.has(tagName)) {
    return null;
  }

  const sanitizedChildren = Array.from(element.childNodes)
    .map(sanitizeNode)
    .filter((child): child is Node => child !== null);

  if (!ALLOWED_TAGS.has(tagName)) {
    const fragment = document.createDocumentFragment();
    for (const child of sanitizedChildren) {
      fragment.appendChild(child);
    }
    return fragment;
  }

  const sanitizedElement = document.createElement(tagName.toLowerCase());
  if (tagName === "A") {
    const href = element.getAttribute("href");
    if (href) {
      try {
        const url = new URL(href, window.location.origin);
        if (ALLOWED_LINK_PROTOCOLS.has(url.protocol)) {
          sanitizedElement.setAttribute("href", href);
          sanitizedElement.setAttribute("target", "_blank");
          sanitizedElement.setAttribute("rel", "noopener noreferrer");
          const ariaLabel = element.getAttribute("aria-label");
          if (ariaLabel) {
            sanitizedElement.setAttribute("aria-label", ariaLabel);
          }
        }
      } catch {
        // Drop malformed href values.
      }
    }
  }

  for (const child of sanitizedChildren) {
    sanitizedElement.appendChild(child);
  }

  return sanitizedElement;
}

export function sanitizeAgentMessageHtml(
  value: string | null | undefined,
): string {
  if (!value) return "";

  const template = document.createElement("template");
  template.innerHTML = value;
  const fragment = document.createDocumentFragment();

  for (const child of Array.from(template.content.childNodes)) {
    const sanitized = sanitizeNode(child);
    if (sanitized) {
      fragment.appendChild(sanitized);
    }
  }

  const container = document.createElement("div");
  container.appendChild(fragment);
  return container.innerHTML === "<p><br></p>" ? "" : container.innerHTML;
}
